import React, { useEffect, useState, useContext } from "react";
import { Footer, HeaderNavi, ScrollTop, MyPageLeftMenuStudent } from "@components";
import { HeaderContext } from "@context/HeaderContext";
import { useNavigate } from "react-router-dom";
import { get, put } from "@utils/axios_api";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Input } from "@components/form";
import { FaEye, FaEyeSlash } from "react-icons/fa";

import { AuthContext } from '@context';

const phoneRegExp = /^(\d{2,3})-(\d{3,4})-(\d{4})$/;

const schema = yup.object().shape({
  password: yup
    .string()
    .nullable()
    .notRequired()
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{5,20}$/,
      "비밀번호는 5~20자 이내로 영문, 숫자, 특수문자를 포함해야 합니다."
    ),
  passwordConfirm: yup
    .string()
    .nullable()
    .notRequired()
    .oneOf([yup.ref("password"), null], "비밀번호가 일치하지 않습니다."),
  email: yup
    .string()
    .email("유효한 이메일을 입력하세요.")
    .required("이메일을 입력하세요."),
  phoneNo: yup
    .string()
    .matches(phoneRegExp, "유효한 전화번호를 입력하세요.")
    .test("len", "전화번호는 최소 9자리 이상이어야 합니다.", (val) => {
      const digits = val.replace(/\D+/g, "");
      return digits.length >= 9 && digits.length <= 11;
    })
    .required("전화번호를 입력하세요."),
  address2: yup.string().required("주소 선택 및 상세주소를 입력하세요."),
 // zipcode: yup.string().required("주소를 선택하세요."),
});

const MyStudentProfile = () => {
  const { auth } = useContext(AuthContext);
  const { subMenu, setSubMenu } = useContext(HeaderContext);
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false); // 패스워드 보이기
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false); // 패스워드 확인 보이기

 /* react-hook-form */
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      memberId: "",
      memberNm: "",
      birthDt: "",
      gender: "", 
      password: "",
      passwordConfirm: "",
      phoneNo: "",
      email: "",
      zipcode: "",
      address1: "",
      address2: "",
      departmentNm: "",
      positionNm: "",
      /* 조회 전용 필드 */
      companyNm: "",
      bizRegNo: "",
      repName: "",
      companyPhoneNo: "",
      bizType: "",
      bizItem: "",
      companyFullAddress: "",
    }
  });

  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = methods;

  useEffect(() => {
    // 페이지 로드 시 서브 메뉴 설정
    setSubMenu("개인정보관리");
    if (!auth.isAuthenticated) {
      // 로그인 안 됐으면 API 호출 안 함
      return;
    }


   /* 회원 정보 조회 */
    const fetchMemberInfo = async () => {
      try {
        const response = await get("/myStudentProfile");
        if (response.status) {
          const data = response.result;

          /* 회사 주소 합성 */
        const fullAddress = `[${data.bizZipcode || ""}] ${data.bizAddress1 || ""}, ${data.bizAddress2 || ""}`;
        // 2) companyFullAddress에 할당
        setValue("companyFullAddress", fullAddress);


          /* 필드 매핑 */
          Object.entries(data).forEach(([k, v]) => setValue(k, v));
          setValue("gender", data.genderCd || ""); // ★ 수정 : gender값 반영
        } else {
          alert("회원 정보를 가져오지 못했습니다.");
        }
      } catch (e) {
        console.error("회원 정보 조회 오류:", e);
      }
    };
    fetchMemberInfo();
  }, [setSubMenu, setValue, auth.isAuthenticated]);

  const onSubmit = async (form) => {
    if (!window.confirm("수정하시겠습니까?")) return;

    /* passwordConfirm 필드는 서버에 전달하지 않음 */
    const { passwordConfirm, ...payload } = form; // ★ 수정

    try {
      const response = await put("/updateStudentProfile", payload);
      if (response.status) {
        alert(response.message || "수정되었습니다");
        navigate("/myLectures/myStudentProfile");
      } else {
        alert(response.message || "회원 정보 수정에 실패했습니다.");
      }
    } catch (e) {
      console.error("회원 정보 수정 오류:", e);
      alert(
        e.response?.data?.message ||
          "회원 정보 수정 중 오류가 발생했습니다. 다시 시도해주세요."
      );
    }
  };
  const onError = (errors) => {
    alert("입력한 정보를 확인해주세요.");
  };

  const handleAddressSearch = () => {
    new window.daum.Postcode({
      oncomplete: function (data) {
        setValue("zipcode", data.zonecode, { shouldValidate: true });
        setValue("address1", data.address, { shouldValidate: true });
      },
    }).open();
  };

  return (
    <>
      <ScrollTop />
      <main id="main" className="mainpage">
        <div>
          <div className="section main-contact">
            {/* 헤더 네비게이션 */}
            <HeaderNavi selectedCategory={subMenu} />

            <div className="sub-container">
              <div className="sub-layout">
                {/* 왼쪽 메뉴 */}
                <MyPageLeftMenuStudent />

                {/* 본문 컨텐츠 */}
                <div className="sub-contact">
                  <h3 className="border-bt">개인정보관리</h3>
                  <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit, onError)}>
                      <div className="lineTop_tb01">
                        <table className="lineTop_tb2 input-tb-pd">
                          <colgroup>
                            <col style={{ width: "150px" }} />
                            <col />
                            <col style={{ width: "150px" }} />
                            <col />
                          </colgroup>
                          <tbody>
                            <tr>
                              <th>아이디</th>
                              <td>
                                <div style={{marginBottom:'20px'}}>
                                <Input
                                  name="memberId"
                                  disabled
                                  className="inp-sel-tb-ipt input-disabled" 
                                />
                                </div>
                              </td>
                              <th>생년월일</th>
                              <td>
                              <div style={{marginBottom:'20px'}}>
                                <Input
                                  name="birthDt"
                                  disabled
                                  className="inp-sel-tb-ipt input-disabled"
                                />
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <th>이름</th>
                              <td>
                                <div style={{marginBottom:'20px'}}>
                                <Input
                                  name="memberNm"
                                  disabled
                                  className="inp-sel-tb-ipt input-disabled"
                                />
                                </div>
                              </td>
                              <th>성별</th>
                              <td>
                                <Controller
                                  name="gender"
                                  control={control}
                                  defaultValue=""
                                  render={({ field }) => (
                                    <div className="fom-item">
                                      <label>
                                        <input
                                          type="radio"
                                          value="M"
                                          checked={field.value === "M"}
                                          disabled
                                        />
                                         <span className="highlight"> 남자</span>
                                      </label>
                                      <label className="ml10">
                                        <input
                                          type="radio"
                                          value="F"
                                          checked={field.value === "F"}
                                          disabled
                                        />
                                         <span className="highlight"> 여자</span>
                                      </label>
                                    </div>
                                  )}
                                />
                              </td>
                            </tr>
                            <tr>
                              <th>
                                비밀번호
                              </th>
                              <td>
                                <div className="password-input-wrapper" style={{marginBottom:'20px'}}>
                                  <Input
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="새 비밀번호를 입력하세요"
                                    errors={errors.password}
                                    className="inp-sel-tb-ipt"
                                  />
                                  <span
                                    className="password-toggle-icon"
                                    onClick={() => setShowPassword(!showPassword)}
                                  >
                                  </span>
                                </div>
                              </td>
                              <th>
                                전화번호
                              </th>
                              <td>
                              <div style={{marginBottom:'20px'}}>
                              <Input
                                  name="phoneNo"
                                  placeholder="ex)010-0123-4567"
                                  className="inp-sel-tb-ipt"
                                  style={{ width: "100%" }}
                                  rules={{
                                    required: "전화번호를 입력하세요.",
                                    pattern: {
                                      value: /^\d{2,3}-\d{3,4}-\d{4}$/,
                                      message: "전화번호는 숫자와 하이픈(-)으로 구성된 형식이어야 합니다. ex) 010-1234-5678"
                                    }
                                  }}
                                  />
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <th>
                                비밀번호 확인
                              </th>
                              <td>
                                <div className="password-input-wrapper" style={{marginBottom:'20px'}}>
                                  <Input
                                    name="passwordConfirm"
                                    type={
                                      showPasswordConfirm ? "text" : "password"
                                    }
                                    placeholder="비밀번호를 다시 입력하세요"
                                    errors={errors.passwordConfirm}
                                    className="inp-sel-tb-ipt"
                                  />
                                  <span
                                    className="password-toggle-icon"
                                    onClick={() =>
                                      setShowPasswordConfirm(!showPasswordConfirm)
                                    }
                                  >      
                                  </span>
                                </div>
                              </td>
                              <th>
                                이메일
                              </th>
                              <td>
                                <div className="fom-item" style={{marginBottom:'20px'}}>
                                  <Input
                                    name="email"
                                    placeholder="이메일을 입력하세요"
                                    errors={errors.email}
                                    className="inp-sel-tb-ipt"
                                  />
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <th>
                                주소
                              </th>
                              <td colSpan="3">
                              <div style={{marginBottom:'20px'}}>
                                <div className="fom-item mb5">
                                  <Input
                                    name="zipcode"
                                    placeholder="우편번호"
                                    readOnly
                                    className="inp-sel-tb-ipt mr5"
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-blue2 ml5"
                                    onClick={handleAddressSearch}
                                  >
                                    주소검색
                                  </button>
                                </div>
                                <div className="dis-f">
                                  <Input
                                    name="address1"
                                    placeholder="주소"
                                    readOnly
                                    className="inp-sel-tb-ipt mr5"
                                  />
                                  <Input
                                    name="address2"
                                    placeholder="상세주소를 입력하세요"
                                    className="inp-sel-tb-ipt ml5"
                                  />
                                </div>
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <th>소속업체</th>
                              <td>
                                <Input
                                  name="companyNm"
                                  disabled
                                  className="inp-sel-tb-ipt input-disabled"
                                />
                              </td>
                              <th>사업자등록번호</th>
                              <td>
                                <Input
                                  name="bizRegNo"
                                  disabled
                                  className="inp-sel-tb-ipt input-disabled"
                                />
                              </td>
                            </tr>
                            <tr>
                              <th>소속부서</th>
                              <td>
                                <Input
                                  name="departmentNm"
                                  className="inp-sel-tb-ipt"
                                />
                              </td>
                              <th>직책</th>
                              <td>
                                <Input
                                  name="positionNm"
                                  className="inp-sel-tb-ipt"
                                />
                              </td>
                            </tr>
                            <tr>
                              <th>대표자명</th>
                              <td>
                                <Input
                                  name="representNm"
                                  disabled
                                  className="inp-sel-tb-ipt input-disabled"
                                />
                              </td>
                              <th>업체전화번호</th>
                              <td>
                              <Input
                                  name="bizPhoneNo"
                                  disabled
                                  className="inp-sel-tb-ipt input-disabled"
                                />
                              </td>
                            </tr>
                            <tr>
                              <th>업태</th>
                              <td>
                                <Input
                                  name="bizTypeNm"
                                  disabled
                                  className="inp-sel-tb-ipt input-disabled"
                                />
                              </td>
                              <th>업종</th>
                              <td>
                                <Input
                                  name="bizItemNm"
                                  disabled
                                  className="inp-sel-tb-ipt input-disabled"
                                />
                              </td>
                            </tr>
                            <tr>
                              <th>사업장주소</th>
                              <td colSpan="3">
                                <Input
                                  name="companyFullAddress"
                                  disabled
                                  className="inp-sel-tb-ipt input-disabled"
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div className="text-cent">
                        <button
                          type="submit"
                          className="btn btn-blue mt30 btn-big"
                        >
                          정보수정
                        </button>
                      </div>
                    </form>
                  </FormProvider>
                </div>
                {/* 본문 컨텐츠 끝 */}
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </main>
    </>
  );
};

export default MyStudentProfile;
